/**
 * PATH: packages/ui/src/kanban/components/card-detail/linkedEmails.ts
 * FILE_ID: UI-KANBAN-LINKED-EMAILS-ORDER-001
 * PURPOSE: Pure ordering for the emails linked to a card — primary first, then
 *          related, de-duplicated, with nulls/blanks dropped.
 * WHY:     A card carries one `primary_email` + N `related_emails`; the Card-tab
 *          email box must list them in a stable order without ever showing the
 *          primary twice (legacy rows can list the primary inside
 *          `related_emails` too). Operator 2026-06-29: "the ability to add
 *          multiple emails in the kanban card is not working properly" — every
 *          linked email has to be enumerable, not just the primary.
 * FLOW:
 *   ┌──────────────────────┐    ┌──────────────────┐    ┌────────────────┐
 *   │ primary_email +      │ →  │ dedupe + drop    │ →  │ ordered id[]   │
 *   │ related_emails[]     │    │ blanks, primary  │    │ (primary 1st)  │
 *   └──────────────────────┘    │ first            │    └────────────────┘
 *                               └──────────────────┘
 */

/**
 * Build the de-duplicated, primary-first list of email ids linked to a card.
 *
 * @param primary the card's `primary_email` id (or null/undefined)
 * @param related the card's `related_emails` id list (or null/undefined)
 * @returns ordered unique ids — primary first, then related in given order,
 *          with empty/blank ids and duplicates removed.
 */
export function orderLinkedEmailIds(
  primary: string | null | undefined,
  related: readonly string[] | null | undefined,
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  const push = (id: string | null | undefined) => {
    const value = (id ?? '').trim()
    if (!value || seen.has(value)) return
    seen.add(value)
    out.push(value)
  }
  push(primary)
  for (const id of related ?? []) push(id)
  return out
}
